import { useState } from 'react';
import { Building2, Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface OpcaoEmpresa {
  id: string;
  nome: string;
  /** Texto à direita do nome (ex.: papel "Admin"/"Usuário"). */
  detalhe?: string;
}

const normalizar = (texto: string) => texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * Seletor de empresa do cabeçalho com busca (2026-10-26): clica, digita parte do nome e filtra (sem diferenciar
 * maiúsculas nem acentos). Lista em ordem alfabética; `opcaoTodas` aparece sempre no topo (filtro global do superadmin).
 */
export function EmpresaSeletor({
  empresas,
  value,
  onChange,
  opcaoTodas,
}: {
  empresas: OpcaoEmpresa[];
  /** id selecionado; `null` = a opção "todas" (quando existe). */
  value: string | null;
  onChange: (id: string | null) => void;
  /** Rótulo da opção "todas" (ex.: "Todas as empresas"); sem ele, não há essa opção. */
  opcaoTodas?: string;
}) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');

  // Lista pequena (empresas da pessoa): ordenar a cada render custa pouco e não depende de memo no componente pai.
  const ordenadas = [...empresas].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' }));
  const termo = normalizar(busca.trim());
  const filtradas = termo ? ordenadas.filter((e) => normalizar(e.nome).includes(termo)) : ordenadas;
  const selecionada = empresas.find((e) => e.id === value);
  const rotulo = selecionada?.nome ?? (value === null && opcaoTodas ? opcaoTodas : 'Empresa');

  const escolher = (id: string | null) => {
    // Escolher a que já está selecionada só fecha: trocar de novo recriaria a sessão e as telas recarregariam à toa.
    if (id !== value) onChange(id);
    setAberto(false);
    setBusca('');
  };

  return (
    <Popover open={aberto} onOpenChange={(abrir) => { setAberto(abrir); if (!abrir) setBusca(''); }}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={aberto}
          aria-label={`Selecionar empresa (atual: ${rotulo})`}
          className="mr-2 h-[34px] w-[149px] justify-between px-3 text-sm font-normal sm:w-[234px]"
        >
          <span className="flex min-w-0 items-center">
            <Building2 className="mr-1.5 h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{rotulo}</span>
          </span>
          <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="end">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Buscar empresa..." value={busca} onValueChange={setBusca} />
          <CommandList>
            <CommandEmpty>Nenhuma empresa encontrada.</CommandEmpty>
            <CommandGroup>
              {opcaoTodas && !termo && (
                <CommandItem value="__todas__" onSelect={() => escolher(null)}>
                  <Check className={cn('mr-2 h-4 w-4', value === null ? 'opacity-100' : 'opacity-0')} />
                  {opcaoTodas}
                </CommandItem>
              )}
              {filtradas.map((e) => (
                <CommandItem key={e.id} value={e.id} onSelect={() => escolher(e.id)}>
                  <Check className={cn('mr-2 h-4 w-4 shrink-0', value === e.id ? 'opacity-100' : 'opacity-0')} />
                  <span className="flex min-w-0 flex-1 items-center justify-between gap-3">
                    <span className="truncate">{e.nome}</span>
                    {e.detalhe && <span className="shrink-0 text-xs text-muted-foreground">{e.detalhe}</span>}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
